<!-- Localized -->
<template>
  <div
    :class="['case-creation-flow h-full flex flex-col max-h-full overflow-hidden flex-grow', {'add-entity-workflow': addEntityWorkflow }]"
  >
    <div v-if="isLoading" class="p-10 flex items-center justify-center w-full">
      <Loader />
    </div>
    <template v-else>
      <div v-if="isPackageListShown" 
        class="flex overflow-hidden h-full w-full">
        <PackageList
          :loading="loading"
          :list="packageList"
          :packageLoaders="packageLoaders"
          @select="selectPackage($event.id)"
        />
      </div>
      <div v-else class="flex flex-col w-full h-full overflow-hidden">  
        <NavBar
          v-if="!addEntityWorkflow"
          :timeline="timelineData"
          :no-cancel="isRevettingMode"
          @back="onBack"
          @cancel="onCancel"
        />
        <neo-layout class="case-creation__tabs--wrapper">
          <Tabs
            v-if="!addEntityWorkflow"
            :tabs="getTabsList"
            :selected="activeTab"
            @select="selectTab"
            class="case-creation__tabs"
            noContent
          />
          <neo-layout>
            <div
              class="case-creation__tabs--tab-content"
            >
              <EntityBar
                v-if="activeTab == 'input' && !addEntityWorkflow && nonCaseEntityList.length"
                :showCaseEntity="addEntityWorkflow"
                :loading="formStates.loading || loaders.redirection"
                :items="computedEntitiesList"
                :readonly="currentStepIndex > 1"
                :plus-disabled="!isAddAvailable"
                :show-plus="addEntityWorkflow"
                @remove="removeEntity"
                @select="onSelectEntity($event.id)"
                @add="showAddEntity"
                :handleEntityChangeStatus="handleEntityChangeStatus"
              />
              <div class="case-creation__body flex flex-col flex-1 w-full h-full overflow-hidden gap-5">
                <div
                  v-if="loaders.entity || loaders.step"
                  class="p-10 flex items-center justify-center w-full"
                >
                  <Loader />
                </div>
                <template v-else>
                  <template
                    v-if="checkCurrentStep(CASE_CREATION_STEPS.ENTITY_DETAILS)"
                  >
                    <FormBuilder
                      ref="case-creation-form-builder"
                      v-if="isFormBuilderShown"
                      v-bind="formBuilderConfig"
                      @removeBlockMethod="selectedEntityFormListInstance.API.removeBlock"
                      @removeFieldMethod="selectedEntityFormListInstance.API.removeField"
                      @submit="() => submitEntityForm(true)"
                      @exit="exitCaseCreation"
                    >
                      <template v-slot:navigation="slotProps">
                        <FormBuilderNavBar
                          v-bind="slotProps"
                          :show-back-button="currentEntityFormIndex > 0"
                          @back="selectPreviousEntityFormSection"
                        />
                      </template>
                    </FormBuilder>

                    <div
                      v-else-if="loaders.redirection && !redirectionScreen"
                      class="p-10 flex items-center justify-center w-full"
                    >
                      <Loader />
                    </div>
                    <RedirectionScreen
                      v-else-if="redirectionScreen?.redirection_config"
                      :screenData="redirectionScreen.redirection_config"
                      :case-id="tempCaseId"
                      :entity-id="selectedEntity?.id"
                      :case-entities="caseEntities" 
                      :case-entities-data="caseEntitiesData"
                      :entity-checks="currentEntityCheckList"
                      :loaders="redirectionScreen.loaders"
                      @submit="submitRedirectionScreen(redirectionScreen)"
                      @back="navigateRedirectionScreenBack"
                      @error="onRedirectionScreenError($event, redirectionScreen)"
                    />

                    <EntityChangeNav
                      v-else-if="showEntityChangeNav"
                      class="flex-1"
                      :add-options="entitiesToAdd"
                      :allow-next="allowNextEntity" 
                      :allow-submit="allowSubmitForm"
                      @add="addEntity"
                      @next="selectNextEntity"
                      @submit="nextStep"
                    />
                  </template>

                  <RedirectionPopup
                    v-if="redirectionPopup"
                    @submit="submitRedirectionScreen(redirectionPopup)"
                    @cancel="cancelRedirectionScreen(redirectionPopup)"
                    @error="onRedirectionScreenError($event, redirectionPopup)"
                  />
                </template>
              </div>
            </div>
          </neo-layout>
        </neo-layout>
      </div>
    </template>

    <ModalConfirm ref="confirm-popup" />

    <ModalConfirm ref="finish-popup">
      <template #content>
        <ul class="submit-confirmation-text">
          <li>{{ $t('case_creation.sure_added_all') }}</li>
        </ul>
      </template>
    </ModalConfirm>

    <ModalConfirm ref="confirmcase-popup" />

    <CaseScoreBreakdown
      v-if="caseEntities.length > 1"
      :caseTypeEntityId="caseTypeEntityId"
      :caseEntities="caseEntities"
    />

    <duplicate-entity 
      :showDuplicateEntity="showDupicateEntity" 
      :rows="rows" 
      :selectedEntity="selectedEntity"
      :entityName="entityName"
      :loadingState="loadingState.pendingApproval"
      :isDeletingEntity="isDeletingEntity"
      :isCreatingEntity="isCreatingEntity"
      :isSubmitDisabled="isSubmitDisabled"
      @discard="discardEntity"
      @save="saveEntity"
      @closed="onClose"
    />
  </div>
</template>

<script>
// * Currently, we use only one step for Case Creation Flow. All behavior is managed by redirections
// * For now, you can find removed code at "./backup". It will be removed later

// TODO check if non-first step/entity may be selected initially
// TODO remove all extra route redirections in watchers and computed properies (these which are already perdormed in the pages/case-create/index.vue component)

import axios from "@/axios";
import { mapMutations, mapActions, mapGetters } from "vuex";
import ModalConfirm from "@shared/modal-confirm/index.vue";
import Loader from "@shared/loader/index.vue";
import FormBuilder from "@shared/components/form-builder";
import {
  NavBar,
  PackageList,
  EntityChangeNav,
  CaseScoreBreakdown,
  duplicateEntity,
  FormBuilderNavBar,
} from "./components";
import { EntityBar } from "@shared/case-entities";
import { Screen, Popup } from "@shared/RedirectionScreen";
import NeoLayout from "@shared/components/neo-layout";
import Tabs from "@shared/dashboard-components/tabs";
import {
  EntityFormList,
  getEntitiesCounter,
  getEntitiesToAdd,
} from "./utils";
import {
  checkPermission,
  getShortEntityData,
  getEntityTypeName, 
  getEntityStatuses
} from "@shared/utils/functions";
import {
  CASE_CREATION_STEPS,
  CASE_CREATION_STEPS_ENTITY_FLOW,
  CASE_CREATION_TABS,
  FORM_SECTION_STATUS_KEY,
  CLIENT_FORM_SECTION_STATUS_KEY,
} from "./utils/constants.js";
import { appList } from '@shared/components/form-builder/utils/index.js';
import {
  subScreenList,
  redirectionDisplayVariants,
} from "@shared/RedirectionScreen/utils";
import {
  getRedirectionDisplay,
  getCurrentScreenSettings,
} from "@shared/RedirectionScreen/utils/functions";
import { ENTITY_TYPE_NAMES, userType } from "@shared/utils/constants.js";
import handleError from "@shared/mixins/handleError.js";
import entityFormAPI from "@shared/case-creation/CaseCreationFlow/utils/entity-form-api.js";
import { prefillFormPreview } from "@shared/case-entities/utils";
import { PermissionError } from "@shared/utils/permissions/index.js";

import {
  checkAllEntityFormsSubmitted,
} from "./utils/entity-form-utils"
import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation";
import { RedirectionConfig } from "./utils/redirection-config";
import { getIntegratedToolName } from "@shared/tools/utils/helpers";


export default {
  name: "case-creation-flow",

  components: {
    NeoLayout,
    Tabs,
    NavBar,
    EntityBar,
    PackageList,
    Loader,
    FormBuilder,
    EntityChangeNav,
    ModalConfirm,
    RedirectionScreen: Screen,
    RedirectionPopup: Popup,
    CaseScoreBreakdown,
    FormBuilderNavBar,
    duplicateEntity,
  },

  mixins: [handleError],

  props: {
    tenantId: {
      type: String,
      required: true,
    },
    clientId: {
      type: String,
      required: true,
    },
    // is used when packages are loading:
    loading: {
      type: Boolean,
      default: false,
    },
    preselectedPackageId: {
      type: String,
    },
    packageList: {
      type: Array,
      default: () => [],
    },
    // is true for not a case creation flow
    addEntityWorkflow: {
      type: Boolean,
      default: false,
    },
    addEntityWorkflowOptions: {
      type: Object,
      default: () => ({
        silentEntityAddition: false
      })
    },
    isSubmitDisabled: {
      type: Boolean,
      default: false,
    },
  },

  inject: {
    app: "currentApp",
    isCurrentAppAllowed: "isCurrentAppAllowed",
  },

  provide () {
    return {
      sharedCaseState: () => this.sharedCaseState,
    }
  },

  data() {
    return {
      FORM_SECTION_STATUS_KEY,
      tabs:  CASE_CREATION_TABS,
      appList,
      redirectionDisplayVariants,
      isLoading: false, // global loader for this component
      CASE_CREATION_STEPS,
      isCaseSubmitted: false, // use to trigger submitCaseCallback after all actions are finished
      selectedPackageId: null,
      duplicateModal: "duplicate-entity",
      tempCaseId: null,
      caseStatus: null,
      caseTypeEntityId: null,
      caseEntities: [],
      caseEntitiesData: null,
      caseEntityForms: {},
      currentEntityFormIndex: 0, // use to track current entity form index
      caseEntityChecks: {},
      isDeletingEntity: false,
      isCreatingEntity: false,
      loaders: {
        case: false,
        caseSubmission: false,
        form: false, // use to set loading status for the current form
        redirection: false, // use for redirection config fetching
        entity: false,
        step: false,
      },
      addEntityLoaders: {},
      formStates: {
        loading: false,
        submitted: false,
      },
      isDuplicateCheckInProgress: false,
      packageLoaders: {},
      forceShowEntityChangeNav: false, // forces Entity Change Nav show
      isShowAddEntityOnly: false, // is only for entities adding
      currentCaseCreationStep: null,
      redirectionConfig: null,
      currentEntityRedirectionScreenHistory: null, // temp history to allow navigation between steps (screens)
      label: [],
      progress:false,
      progressBarLoading:{
        loading:false
      },
      createtionProgressBar: false,
      permissionErrors: [], // collect to not repeat same errors display
      rows: [],
      duplicateEntityPayload: [],
      entityName: "",
      isDuplicate: false,
      loadingState: {
                pendingApproval: false,
      },
      existingEntity:  "",
      showDupicateEntity: false,
      isRedirectionConfigBeforeForms: false, // use to check if redirection config is fetched before forms submission to build proper navigation
    };
  },

  computed: {
    ...mapGetters({
      getRiskScoreData: "CaseCreationStore/getRiskScoreData",
      currentPopupFormData: "CaseCreationStore/getCurrentPopupFormData",
      getNewEntityInfo: "getNewEntityInfo",
      getUserType:"getUserType",
      getCaseEntitiesData:"getCaseEntitiesData",
      getCaseData: "getCaseData",
      getCaseStatusList: "getCaseStatusList",
      refreshConfig: "VersionsStore/getRefreshConfig",
      getIsRevettingStarted: "VersionsStore/getIsRevettingStarted",
    }),
    getPackageId() {
      return this.getCaseData?.client_package_id || null;
    },
    getClientId() {
      return this.getCaseData?.client_id || null;
    },

    formBuilderConfig() {
      const isClient = this.getUserType == userType.CLIENT;
      const config = {
        hideSingleSection: true,
        data: this.currentFormToShow,
        saveSectionMethod: this.saveEntitySection,
        getEntityChangeNav: this.changeToaddEntityFlow,
        formStates: this.formStates,
        case_id: this.tempCaseId,
        actionsNames: this.formBuilderActionsNames,
        entityId: this.selectedEntity?.id,
        selectedEntity: this.selectedEntity,
        readOnlyMode: this.writeAccessToForm,
        app: isClient ? appList.CREATECLIENT : appList.CREATE,
      }

      if (this.isCasePreservationEnabled) {
        config.skipCompletedStatus = isClient ? CLIENT_FORM_SECTION_STATUS_KEY : FORM_SECTION_STATUS_KEY;
      }

      return config;
    },

    isRevettingMode() {
      // TODO
      return !!this.getIsRevettingStarted;
    },
    isCasePreservationEnabled() {
      return !this.isRevettingMode;
    },
    isOneOfRefreshMode() {
      return this.refreshConfig?.mode === "ONE_OFF";
    },

    formBuilderActionsNames() {
      return {
        submit:  this.$t('actions.save'),
        preview: this.$t('form_builder.preview'),
      }
    },
    selectedEntityId: {
      get() {
        return this.$route.query.entityId
      },
      set(entityId) {
        this.$router.push({
          query: { 
            ...this.$route.query, entityId 
          }
        });
      }
    },
    singleTypeCaseEntity() {
      return this.entitiesToAdd?.length === 1 ? this.entitiesToAdd[0] : null;
    },

    getTabsList () {
        return this.tabs.map(tab => {
          if (tab.id === 'overview') {
            tab.visible = !!this.caseEntityFormInstance?.hasFields 
          } else {
            tab.visible = !this.selectedEntityFormListInstance?.hasFields || this.computedEntitiesList?.length > 1;
          }
          tab.label = this.$t(`profile_view.${tab.tKey}`)
          return tab;
        }).filter(x => x.visible);
    },

    activeTab() {
      return this.forceShowEntityChangeNav || !this.isCaseEntity(this.selectedEntityId) ? 'input' : 'overview';
    },

    singleTypeCaseEntityMinExceed() {
        if (!this.singleTypeCaseEntity) return false;
        const { min, count } = this.singleTypeCaseEntity.entity

        return min === 0 || min > 0 && count >= min
    },

    toAddSingleTypeEntity() {
      return this.singleTypeCaseEntity && !this.singleTypeCaseEntityMinExceed;
    },

    showEntityChangeNav() {
      // Don't show Entity Change Nav if we are in the Revetting mode
      return this.forceShowEntityChangeNav && !this.toAddSingleTypeEntity && !this.isRevettingMode;
    },

    sharedCaseState() {
      return {
        case_id: this.tempCaseId,
        tenant_id: this.tenantId,
        client_id: this.clientId,
        package_id: this.selectedPackageId,
        selected_entity_id: this.selectedEntityId,
        selected_entity: this.selectedEntity,
        redirection_config: this.redirectionConfig,
        current_workflow_object_id: this.currentWorkflowObjectId,
        is_case_submitted: this.isCaseSubmitted,
        case_entities: this.caseEntities,
        case_entities_data: this.caseEntitiesData,
        is_revetting: this.isRevettingMode,
        is_case_preservation: this.isCasePreservationEnabled,
        is_redirection_screen_back_button_shown: this.isRedirectionScreenBackButtonShown,
      }
    },

    isRedirectionScreenBackButtonShown() {
      // If redirection screens are shown before the forms, there is nowhere to go back to before the first redirection screen:
      return !(this.isRedirectionConfigBeforeForms && this.currentEntityRedirectionScreenHistory?.history.length <= 1);
    },

    caseEntitiesInfo() {
      return this.caseEntitiesData?.case_entity_info || null;
    },

    caseEntityTypesInfo() {
      return this.caseEntitiesData?.case_entity_type_info || null;
    },

    headerItemsConfig() {
      if (!this.computedCaseData) return null;

      // TODO reference to the case score data

      let statusValue = null;
      const statusData = this.getCaseStatusList;
      if (statusData) {
        const currentCaseStatus = this.getCurrentStatus(statusData);
        statusValue = {
          ...currentCaseStatus,
          value: currentCaseStatus?.status_id,
          label: currentCaseStatus?.status_name,
          color: currentCaseStatus?.status_colour,
          name: translateEntity(currentCaseStatus || {}, caseEntityTypesMap.WORKFLOW)?.status_name
        }
      }

      return {
        name: {
          id: "case_id",
          title: this.$t('case_creation.id'),
          value: this.computedCaseData?.name,
          type: "text",
        },
        status: {
          id: "status",
          title: this.$t('case_creation.status'),
          value: statusValue,
          type: "menu",
          props: {
            options: statusData.filter((stat) => !stat.current).map((stat) => ({
              ...stat,
              value: stat.status_id,
              label: stat.status_name,
              color: stat.status_colour,
              name: translateEntity(stat, caseEntityTypesMap.WORKFLOW)?.status_name
            })),
            showDropdown: checkPermission('case.status_update'),
          }
        },
        score: {
          custom: true,
          type: 'risk-score',
          show: !!this.getRiskScoreData?.label_id && !!(this.isRiskManagementEnabled && this.getRiskScoreData),
          props: {
            case_id: this.tempCaseId,
            caseEntityList: this.caseEntities,
            caseEntity: this.caseEntity,
          }
        },
      }
    },
    headerConfig() {
      if (!this.headerItemsConfig) return null;

      const headerItems = Object.values(this.headerItemsConfig)
        .filter((el) => el.show !== false);

      return {
        title: null,
        list: Object.values(headerItems),
      }
    },
    showCaseCreation() {
      return !!(
        this.selectedPackage &&
        !this.packageLoaders[this.selectedPackageId] &&
        this.computedCaseData
      );
    },
    selectedPackage() {
      return this.selectedPackageId
        ? this.packageList.find((el) => el.id === this.selectedPackageId)
        : null;
    },
    currentStepIndex() {
      // use positive value
      if (!this.currentCaseCreationStep) return 1;
      return (
        this.caseCreationSteps.findIndex(
          (el) => el.id === this.currentCaseCreationStep.id
        ) + 1
      );
    },
    // provide timeline data if needed:
    timelineData() {
      return null;
    },
    caseEntity() {
      // find an entity with the case temp data:
      return (
        this.caseEntities?.find((el) => el.id === this.caseTypeEntityId) || null
      );
    },
    nonCaseEntityList() {
      return this.caseEntities?.filter((el) => !this.isCaseEntity(el?.id)) || [];
    },
    computedCaseData() {
      if (!this.caseEntity) return null;
      return {
        caseEntity: this.caseEntity,
        name: this.caseEntity.entity_name,
        status: this.caseStatus,
      };
    },
    caseCreationSteps() {
      return CASE_CREATION_STEPS_ENTITY_FLOW;
    },
    // editable entities
    computedEntitiesList() {
      if (!this.caseEntity || !this.caseEntities) return null;

      const isEntitySelectable = (el) => this.currentCaseCreationStep.id !== CASE_CREATION_STEPS.ENTITY_DETAILS
        || (this.selectedEntityId !== el.id
        || this.showEntityChangeNav);

      return this.caseEntities.map((el) => {
        return {
          data: el,
          monitoringData: getShortEntityData(this.caseEntitiesData, el), // add data necessary for monitoring tools
          id: el.id,
          collect_data: el.collect_data,
          type: this.caseEntityTypesInfo[el.entity_type_id]?.name,
          name: el.entity_name || "",
          readonly: el.id === this.caseTypeEntityId,
          disabled: false,
          selectable: isEntitySelectable(el),
          deletable: false,
          selected: (el.id === this.selectedEntityId) && !this.isShowAddEntityOnly, // if we force Add Entity screen display don't show entities as selected
          formList: this.caseEntityForms[el.id] || null,
          checks: this.caseEntityChecks[el.id] || null,
          is_main_entity: el.is_main_entity,
          checked: false, // mark with check
          warning: false, // highlight as warning
          statusList: el.statusList
        };
      });
    },
    selectedEntity() {
      if (!this.selectedEntityId || !this.computedEntitiesList) return null;
      return (
        this.computedEntitiesList.find(
          (el) => el.id === this.selectedEntityId
        ) || null
      );
    },
    selectedEntityIndex() {
      return this.getEntityIndex(this.selectedEntity);
    },
    isLastEntity() {
      return this.selectedEntityIndex === this.computedEntitiesList.length - 1;
    },

    caseEntityFormInstance() {
      return this.caseEntityForms?.[this.caseEntity?.id] || null;
    },
    
    selectedEntityFormListInstance() {
      if (!this.selectedEntity || !this.caseEntityForms) return null;

      return this.selectedEntity.formList || [];
    },
    selectedEntityFormList() {
      return this.selectedEntityFormListInstance?.list || null;
    },
    selectedEntityCurrentFormListItem() {
      if (!this.selectedEntityFormList?.length) return null;

      return this.selectedEntityFormList[this.currentEntityFormIndex] || null;
    },
    selectedEntityCurrentForm() {
      return this.selectedEntityCurrentFormListItem?.form || null;
    },
    selectedEntityCurrentFormData() {
      return this.selectedEntityCurrentForm?.formData || null;
    },
    writeAccessToForm(){
      return !this.selectedEntityCurrentFormListItem?.payload?.write_access;
    },
    isNextEntityForm() {
      if (!this.selectedEntityFormList) return false;

      // check if next form is available in initial form list:
      return this.currentEntityFormIndex < this.selectedEntityFormList.length - 1;
    },

    entitiesCounter() {
      // calculate dynamic counts considering newly created entities
      if (!this.caseEntitiesData || !this.caseEntities) return null;

      return getEntitiesCounter(this.caseEntitiesData, this.caseEntities);
    },
    isAllCaseFormsSubmitted() {
      return Object.values(this.caseEntityForms).every((el) => !!el.isAllSubmitted);
    },
    isAllCaseFormsStored() {
      return Object.values(this.caseEntityForms).every((el) => !!el.isStored);
    },
    isNextEntity() {
      const index = this.getNextEntityIndex();
      return !!index;
    },
    isNextStep() {
      // steps numbering starts from 1 so it already contains +1 when we change the selected step:
      return !!this.caseCreationSteps[this.currentStepIndex];
    },

    /**
     * check if all entitiy's forms are submitted so we can move to the next entity
     */
    allowNextEntity() {
      if (!this.entitiesCounter?.entities) return false;
      return (
        this.isNextEntity &&
        this.selectedEntityFormListInstance.isAllSubmitted &&
        !this.isShowAddEntityOnly // don't show "next" when we open a screen intended only for the new entity adding (click on "Plus" button)
      );
    },

    /**
     * check if the current entity form list can be submitted
     */
    allowSubmitForm() {
      // unlike the "next" button, this one can be displayed regardless of the call source
      if (!this.entitiesCounter?.entities || !!this.allowNextEntity || this.addEntityWorkflow) return false;
      return (
        this.isAllCaseFormsStored &&
        !Object.values(this.entitiesCounter.entities).some(
          (el) => el.count < el.min
        )
      );
    },
    entitiesToAdd() {
      if (!this.entitiesCounter?.entities) return [];

      const entitiesToAdd = getEntitiesToAdd(this.entitiesCounter);
      return entitiesToAdd.map((el) => ({
        ...el,
        tName: this.$t(`components.light_ray.${(el.name || '').toLowerCase()}`), 
        loading: this.addEntityLoaders[el.id] || false,
      }));
    },
    isAddAvailable() {
      return !!this.entitiesToAdd.length && this.currentCaseCreationStep.id === CASE_CREATION_STEPS.ENTITY_DETAILS;
    },
    currentEntityCheckList() {
      return this.caseEntityChecks[this.selectedEntityId] || [];
    },

    isCaseEntitySelected() {
      return this.isCaseEntity(this.selectedEntityId);
    },

    isRiskManagementEnabled() {
      return checkPermission('caseRiskScore.read');
    },

    redirectionScreen() {
      return this.redirectionConfig?.[redirectionDisplayVariants.SCREEN] ?? null;
    },
    redirectionPopup() {
      return this.redirectionConfig?.[redirectionDisplayVariants.POPUP] ?? null;
    },
    // check if any redirection screen is shown
    isRedirection() {
      if (!this.redirectionConfig) return false;

      return Object.values(this.redirectionConfig).some(Boolean); 
    },
    currentWorkflowObjectId() {
      // use Case ID if we need to check redirection config after case submission
      if (this.isCaseEntitySelected || this.isCaseSubmitted) return this.tempCaseId;
      // if (this.isCaseEntitySelected) return this.tempCaseId; // TODO check if we need to use this ID for the case entity
      return this.selectedEntityId ?? null;
    },

    /**
     * Returns the current form data to show in the Form Builder.
     * It will return the current popup form data if it exists,
     * otherwise it will return the selected entity's current form data.
     */
    currentFormToShow() {
      // TODO consider another solution: show nothing on the background of the State Transition Pop-up
      return this.currentPopupFormData
        || this.selectedEntityCurrentFormData
    },

    isFormBuilderShown() {
      return this.currentFormToShow
        && !this.showEntityChangeNav
        && !this.redirectionScreen
        && !this.loaders.redirection;
    },

    computedSelectedPackageId: {
      get() {
        return this.$route.query.packageid || this.preselectedPackageId || null;
      },
      set(value) {
        this.selectedPackageId = value;
        this.$router.push({
          query: { ...this.$route.query, packageid: value },
        });
      }
    },
    computedSelectedClientId: {
      get() {
        return this.$route.query.client || this.clientId || null;
      },
      set(value) {
        this.setClientIdParam(value);
      }
    },

    isPackageListShown() {
      return !this.addEntityWorkflow && (!this.selectedPackageId || this.packageLoaders[this.selectedPackageId]) && !this.tempCaseId;
    }
  },

  async mounted() {
    this.isLoading = true;
    // refetch case package data if case already created (temCaseId exists)
    await this.refetchCasePackage();

    const packageid = this.preselectedPackageId;
    this.resetPackage(packageid || null);
    if (this.preselectedPackageId) {
      // show a loader while fetching temp data for initially selected package
      // because loader of a particular package card isn't shown
      await this.selectPackage(this.preselectedPackageId);

      if (this.addEntityWorkflow) {
        this.forceShowEntityChangeNav = !this.addEntityWorkflowOptions.silentEntityAddition
      }
    }
    this.isLoading = false;

    if (this.checkCaseSubmitted()) return;

    if(!this.isCaseEntitySelected)
      this.selectedEntityCurrentFormData?.sections?.forEach( sec => {
      this.$set(this.duplicateEntityPayload, this.duplicateEntityPayload.length, {
        section_id:sec.section_id,
        data: sec,
        finalSubmit: false,
      });
    });
  },

  created() {
    this.tempCaseId = this.$route.query?.caseid || null;
    this.resetCreationStep();

    // no need to show alert because all data is stored to the server:
    // window.addEventListener("beforeunload", this.confirmPageClose);
  },

  async beforeDestroy() {
    // reset header
    this.setHeaderData(null);

    // Cancel revetting on the server:
    if (this.isRevettingMode) {
      await this.switchRevetting(false);
    }

    // no need to show alert because all data is stored to the server:
    // remove event listener
    // window.removeEventListener("beforeunload", this.confirmPageClose);
  },

  watch: {
    showEntityChangeNav(val) {
      // reset on Entity Change Nav close
      if (!val) this.isShowAddEntityOnly = false;
    },
    // update header data on package data change:
    headerConfig: {
      handler() {
        this.setHeaderData(this.headerConfig);
      },
      deep: true,
    },
    // update header data on package data change:
    headerItemsConfig: {
      handler() {
        this.setHeaderData(this.headerConfig);
      },
      deep: true,
    },
    // TODO: Temporary solution, may change in the future
    caseEntities: {
      async handler(val) {
        if (val?.length > 1) {
          await this.fetchRiskScore({ case_id: this.tempCaseId });
          await this.fetchCaseStatus();
        }
      },
      deep: true,
    },
    clientId(val) {
      this.setClientIdParam(val);
    },
  },

  methods: {
    ...mapActions({
      fetchRiskScoreMethod: "CaseCreationStore/fetchRiskScore",
      toggleRiskScoreModal: "CaseCreationStore/toggleRiskScoreModal",
      fetchRiskLabelOptions: "CaseCreationStore/fetchRiskLabelOptions",
      fetchCaseData: "fetchCaseData",
      setSelectedEntity: "setSelectedEntity",
      resetNewEntityInfo: "resetNewEntityInfo",
      fetchRefreshConfiguration: "VersionsStore/fetchRefreshConfiguration",
      getProductMonitoring: "getProductMonitoring",
      startRevetting: "VersionsStore/startRevetting",
      stopRevetting: "VersionsStore/stopRevetting",
      updateRefreshConfiguration: "VersionsStore/updateRefreshConfiguration",
    }),
    ...mapMutations({
      SET_RISK_SCORE_DATA: "CaseCreationStore/SET_RISK_SCORE_DATA",
      SET_CUSTOM_HEADER: "SET_CUSTOM_HEADER",
      SET_SELECTED_ENTITY: "SET_SELECTED_ENTITY",
      SET_REVETTING_STARTED: "VersionsStore/SET_REVETTING_STARTED",
    }),
    checkPermission,

    confirmPageClose(event) {
      const isHistoryData = !!this.currentEntityRedirectionScreenHistory?.history?.length > 1;
      if (isHistoryData) {
        event.preventDefault();
        event.returnValue = '';
      }
    },

    // Redirect to the Case Specific Screen if already submitted:
    checkCaseSubmitted() {
      if (!this.getCaseData || this.isRevettingMode) return false;

      if (this.getCaseData.submitted) {
        const caseid = this.tempCaseId || this.$route.query.caseid;
        const entityId = this.selectedEntityId || this.$route.query.entityId;
        const clientId = this.computedSelectedClientId;
        const case_id_label = this.getCaseData.case_id;

        this.$router.push({
          name: 'Profile Container',
          params: { caseid: case_id_label },
          query: { caseid, entityId, clientId },
        });
        return true;
      }
      return false;
    },
    
    // TODO remove from the component as is already implemented on the higher level:
    setClientIdParam(clientId) {
      this.$router.push({
        query: { ...this.$route.query, client: clientId },
      });
    },
    
    getEntityById(id, raw = false) {
      if (raw) {
        return this.caseEntities.find((el) => el.id === id);
      }
      return this.computedEntitiesList?.find((el) => el.id === id);
    },

    getEntityIndex(entity) {
      return this.computedEntitiesList?.findIndex((el) => el.id === entity.id);
    },

    async selectTab (tab) {
        if (tab === this.activeTab) return;

        const canChangeTab = this.checkSwitchable();

        if (!canChangeTab) return;

        const entityId = this.computedEntitiesList?.findLast(x => !this.isCaseEntity(x.id))

        const isCaseEntity = tab === 'overview';

        const targetEntityId = isCaseEntity ? this.caseEntity.id : entityId?.id;

        await this.onSelectEntity(targetEntityId, {
          init: !isCaseEntity, // set init=true to execute this.handleEntityChangeNav() for the non-case entity
        });
    },

    getEntityTypeName(entity) {
      if (!this.caseEntityTypesInfo) return null;
      return this.caseEntityTypesInfo[entity.entity_type_id].name;
    },

    discardEntity() {
      this.isDeletingEntity = true;
      this.deleteEntity(this.$route.query?.caseid,this.$route.query.entityId)
    },
    async deleteEntity(caseId,entityId) {

      const isCurrent = entityId;
      const currentEntityIndex = this.caseEntities.findIndex(
        (el) => el.id === entityId
      );
      const currentComputedEntityIndex = this.computedEntitiesList.findIndex(
        (el) => el.id === entityId
      );
      try {
           let url =  `/case/${caseId}/entity/${entityId}?duplicate_entity=true`
            let { data } = await axios.delete(url);
            if(data) {
                       
          // select previous entity if removed was selected
          if (isCurrent)
            await this.selectEntity(this.caseEntities[currentComputedEntityIndex - 1].entityId, { hideEntityChangeNav: false, submit: true });
          // remove entity from list:
          this.caseEntities.splice(currentEntityIndex, 1);
          this.caseEntitiesOrder = this.caseEntitiesOrder.filter(x => x!=entityId);
          // remove related form:
          delete this.caseEntityForms[entityId];
          this.$emit("deleteEntity", entityId);
          this.isDeletingEntity = false;
           this.hideModal();
           this.continueCheck();
        }
      } catch (error) {
        this.handleError(error);
      }
    },
    async continueCheck() {
      this.rows = [];
      const promise = await this.$refs["confirmcase-popup"].show({
              title: "Are you sure?",
              message:"Do you wish to continue",
              additionalBtn:"No",
              submitButtonText:"Yes"
          });
     if(!promise) {
            if ((!this.getCaseEntitiesData || 
        !this.getCaseEntitiesData.case_entities || 
        this.getCaseEntitiesData.case_entities.length === 0)  && (this.caseEntities?.length <=1 && this.caseEntities[0].entity_type === 'Case'))  
          {
              await axios.delete(`/case/${this.$route.query?.caseid}`);
              this.exitCaseCreation();
              
              this.$router.push({ name: "dashboard.view" });
          } else {
            this.submitCase();
            this.$router.push({ name: "dashboard.view" });
          }
         
      }
      else {
          if (this.toAddSingleTypeEntity) {
            await this.addEntity({ id: this.singleTypeCaseEntity.id })
        } else {
          this.forceShowEntityChangeNav = true;
        }
      }
      this.duplicateEntityPayload = [];
    },
    showModal() {
            this.$modal.show("duplicate-entity");
    },
    hideModal() {
        this.$modal.hide("duplicate-entity");
    },
    onClose() {
      this.showDupicateEntity = false;
      this.rows = [];
    },

    async handleEntityChangeStatus(payload) {
        const { entity: { id }, status: { status_id } } = payload;
        try {
            const url = `workflow/${id}/status/${status_id}`;
            await axios.post(url);

            // TODO refetch single changed entity status instead full data reset:
            await this.getCaseEntityData();
            this.$toast.success(this.$t('case_creation.entity_status_changed'));
            // await this.showProgressBar(id)
        } catch (error) {
            console.log("error handleEntityChangeStatus :>> ", error);
        }
    },

    // populate header with the current case data:
    setHeaderData(payload = this.headerConfig) {
      if (!this.addEntityWorkflow) this.SET_CUSTOM_HEADER(payload);
    },

    checkCurrentStep(stepId) {
      return this.currentCaseCreationStep.id === stepId;
    },

    async onBack() {
      // currentStepIndex starts from 1 so we need to go to the -2 index in array
      if (this.currentStepIndex > 1) {
        await this.selectStep(
          this.caseCreationSteps[this.currentStepIndex - 2],
          { entityId: this.caseEntities[this.caseEntities.length - 1]?.id }
        );
      } else {
        this.exitCaseCreation();
      }
    },
  
    async onCancel() {
      try {
        const cancelConfirmed = await this.$refs["confirm-popup"].show({
          title: "Are you sure?",
          message: "The case data will be lost.",
        });
        if (!cancelConfirmed) return;
        await this.deleteTempCaseData();
        this.exitCaseCreation();
        this.duplicateEntityPayload = [];
        // this.createtionProgressBar = false
      } catch (error) {
        this.handleError(error);
      }
    },

    getNotSubmittedForm() {
        let id;
        for (const key in this.caseEntityForms) {
          if (!checkAllEntityFormsSubmitted(this.caseEntityForms[key])) {
            id = key;
            break;
          }
        }
        return id;
    },

    async showAddEntity(payload = true) {
      //const isNewAndNotSubmitted = !this.caseEntityForms[this.selectedEntityId].isSubmitted;
      const notSubmittedId = this.getNotSubmittedForm();
      // if the form is new (not filled => invalid) or changed (dirty):
      if (notSubmittedId) {
          this.$toast.error(this.$t('case_creation.please_submit_form'));

          if (notSubmittedId != this.selectedEntityId) {
            await this.selectEntity(notSubmittedId);
          }
          return;
      }

      // const isSwitchable = this.checkSwitchable(notSubmittedId);
      // if (!isSwitchable) return;
      this.forceShowEntityChangeNav = payload;
      this.selectedEntityId = null;
      await this.setRedirectionConfig();
    },
    async deleteTempCaseData() {
      await axios.post(`/case/${this.tempCaseId}/cancel-case-creation-journey`);
    },

    // * Currently, we have only one step - CASE_CREATION_STEPS.ENTITY_DETAILS. Other steps are managed by workflow redirections
    async selectStep(step, { entityId, init = false, initCase = false } = {}) {
      // are called after the current step is changed to the new value
      const initStepMethods = {
        [CASE_CREATION_STEPS.ENTITY_DETAILS]: async () => {
          const entityToSelectId = entityId || this.caseEntity?.id || null;

          // if we are in the addEntityWorkflow, we don't need to init current entity form list
          if (entityToSelectId && !this.addEntityWorkflow) {
            if (entityToSelectId !== this.caseEntity?.id) {
              // we need to fetch entity form list for the case entity to render tabs properly:
              await this.initCurrentEntityFormList({
                entityId: this.caseEntity?.id,
                // submit: true,
                init: true,
              });
            }
            await this.selectEntity(entityToSelectId, { init, initCase });
          }
          // await this.fetchRiskScore({ case_id: this.tempCaseId });
        },
      };

      // reset redirection config:
      await this.setRedirectionConfig();

      this.currentCaseCreationStep = step;

      if (step) {
        const { id } = step;
        try {
          await initStepMethods[id]?.();
        } catch (error) {
          this.handleError(error);
        }
      } else {
        this.$emit("redirectToInput", false)
      }
    },

    async nextStep() {
      // are called before the current step is changed to the new value
      const stepCallbacks = {
        [CASE_CREATION_STEPS.ENTITY_DETAILS]: async () => {
          await this.submitCase();
          return false;
        },
      };

      // manage next step selection:
      let toProceed = true;
      const { id } = this.currentCaseCreationStep;
      try {
        toProceed = await stepCallbacks[id]?.() ?? true;
      } catch (error) {
        this.handleError(error);
      }
      if (!toProceed) return;

      // steps numbering starts from 1 so it already contains +1 when we change the selected step:
      await this.selectStep(this.caseCreationSteps[this.currentStepIndex]);
    },

    resetPackage(payload = null) {
      this.selectedPackageId = payload;
      this.currentCaseCreationStep = this.caseCreationSteps[0];
      this.caseEntitiesOrder = null;
      this.resetCaseCreation();
      // reset header:
      this.setHeaderData(null);

      // if (!payload) {
      //   this.$emit("cancel");
      // }
    },

    resetCaseCreation() {
      this.setSelectedEntity(null);
      this.tempCaseId = null;
      this.caseEntitiesData = null;
      this.caseEntities = [];
      this.caseEntityForms = {};
      this.caseEntityChecks = {};
      this.selectedEntityId = this.$route.query?.entityId || null;
      this.isCaseSubmitted = false;
      this.permissionErrors = [];
      this.resetCurrentEntityFormIndex();
      this.SET_RISK_SCORE_DATA(null);
      // reset redirection config:
      this.setRedirectionConfig();
    },

    resetCreationStep() {
      this.currentCaseCreationStep = this.caseCreationSteps[0];
    },

    exitCaseCreation() {
      this.resetPackage();
      this.$emit("cancel");
      this.SET_RISK_SCORE_DATA(null);
    },

    async initCaseCreationProcess() {
      const entityToSelect = (this.isCasePreservationEnabled
        ? this.computedEntitiesList?.at(-1)?.id
        : this.computedEntitiesList?.[0]?.id) || this.$route.query?.entityId;

      await this.selectStep(this.caseCreationSteps[0], {
        entityId: entityToSelect,
        init: true, // TODO check if can set to false
        initCase: true,
      });
    },

    async selectPackage(packageId) {
      this.selectedPackageId = packageId;
      this.$set(this.packageLoaders, packageId, true);

      try {
        this.resetCreationStep();

        if (this.addEntityWorkflow) {
          await this.addEntityToCase();
        } else {
          this.$router.push({
            query: { ...this.$route.query, packageid: packageId },
          });
          await this.createTempCase();

          // stop case initialization if case is already submitted:
          if (this.checkCaseSubmitted()) return;

          await this.fetchRiskScore({ case_id: this.tempCaseId });

          // if clientId is not set in the query params, set it from the prop
          if (!this.$route.query.client && this.clientId) {
            this.setClientIdParam(this.clientId);
          }
        }
        this.$set(this.packageLoaders, packageId, false);
        this.$emit("select:package", packageId);

        await this.initCaseCreationProcess();
      } catch (error) {
        this.handleError(error);
        this.resetPackage();
        this.$set(this.packageLoaders, packageId, false);
        if(this.app === appList.CLIENT || this.getUserType == userType.CLIENT)
        {
          this.$router.push(this.$route.query?.from || '/');
        }
      }
    },

    checkSwitchable(showMessage = true) {
      // TODO check if switch should be prevented if !!this.redirectionConfig
      const formBuilder = this.$refs?.["case-creation-form-builder"];
      // TODO check this because we can have an empty form:
      if(!this.selectedEntityCurrentFormData || this.selectedEntityCurrentFormData?.sections?.length === 0)
        return false;

      if (!formBuilder) return true;
      const isDirty = formBuilder.isDirty;
      const isInvalid = formBuilder.$v.$invalid;
      // if the form is new (not filled => invalid) or changed (dirty):
      const isSwitchable = !(isDirty || isInvalid);
      if (!isSwitchable && showMessage)
        this.$toast.error(this.$t('case_creation.save_section_data'));
      return isSwitchable;
    },
    
    async fetchEntityFormList(case_entity_id, { submit }) {
      // TODO fetch first 'tenant' form
      const response = await entityFormAPI.fetchEntityFormList({
        case_id: this.tempCaseId,
        entity_id: case_entity_id,
      });

      // init EntityFormList instance:
      const entityFormList = new EntityFormList({
        forms: response.data?.tenant_forms || [],
        entity: this.getEntityById(case_entity_id, true),
      });

      // Store original form list response for later use
      entityFormList.originalFormList = response;

      const sourceEntity = this.getEntityById(case_entity_id, true);
      const isCreationFormsSubmitted = !!sourceEntity?.creation_form_submitted;
      // set isStored flag based on the source entity creation_form_submitted status:
      entityFormList.setStored(isCreationFormsSubmitted);

      this.$set(this.caseEntityForms, case_entity_id, entityFormList);

      // need to submit fetched entity when after entity was removed
      if (submit) this.setAllEntityFormsStored(entityFormList);

      return entityFormList;
    },

    // set entity's forms as stored:
    setAllEntityFormsStored(entityFormListInstance) {
      entityFormListInstance?.submitAllForms();
      entityFormListInstance?.setStored();
    },

    resetCurrentEntityFormIndex() {
      this.setCurrentEntityFormIndex(0);
    },

    /**
     * Move to the next entity form based on the current form index
     * @param {Object} params - Parameters for moving to the next entity form.
     * @param {string} params.init - indicate if we need to re-init the entity checking its forms' states and redirection config. If it is true that means user will automatically be redirected to the last entity creation step.
     */
    async moveToNextEntityForm({
      entityId,
      currentFormIndex = 0,
      init = false,
      preventAutoSubmit = false, // prevent moving to the next form (e.g., executed by user interaction)
    } = {}) {
      const entityIdToUse = entityId || this.selectedEntityId;
      const isCurrentEntity = entityIdToUse === this.selectedEntityId;

      // skip some steps if is called on the backgroud for other entities or the case entity:
      const silent = init && !isCurrentEntity;

      const currentFormIndexToUse = silent ? currentFormIndex : this.currentEntityFormIndex;

      try {
        // get the current entity form list item to use based on entity id:
        const currentComputedEntity = this.getEntityById(entityIdToUse);
        const entityFormListInstance = currentComputedEntity?.formList || null;
        const entityFormList = entityFormListInstance?.list || null;
        const entityCurrentFormListItem = entityFormList?.[currentFormIndexToUse] || null;
        let entityCurrentForm = entityCurrentFormListItem?.form || null;

        if (!entityCurrentFormListItem) {
          // Submit current entity form list if there is no forms to fetch:
          if (!silent) {
            await this.submitEntityFormList();
          }
          return;
        }

        const toFetchCurrent = !!entityCurrentFormListItem.payload
          && !entityCurrentForm;

        const newFormIndex = currentFormIndexToUse + (toFetchCurrent ? 0 : 1);
        const nextFormListItem = entityFormList?.[newFormIndex] || null;
        const nextFormPayload = nextFormListItem?.payload || null;

        // if no next form payload is found, we can't proceed:
        if (!nextFormPayload) {
          // console.warn("No next form payload found"); // debugging
          // Trigger all entity forms submission if we are on the last form:
          this.setAllEntityFormsStored(entityFormListInstance);
          // if we are on the current entity last form, submit entity form list:
          if (isCurrentEntity) {
            // Don't submit if called on the background or during initialization:
            if (!silent && !init) {
              await this.submitEntityFormList();
            } else {
              await this.onEntityFormListSubmitted({ init });
            }
          }
          return;
        }

        const isSubmittedOnServer = nextFormPayload.creation_form_submitted
        // change to show proper form in the UI:
        if (!silent)
          this.setCurrentEntityFormIndex(newFormIndex);

        const requestParams = {};
        const { tenant_check_id } = nextFormPayload;
        if (tenant_check_id) {
          requestParams.tenant_check_id = tenant_check_id;
        }

        await this.fetchEntityForm(entityIdToUse, requestParams);

        // update the current form based on the fetched form list item:
        entityCurrentForm = nextFormListItem?.form || null;

        const moveToNextEntityFormCallback = async () => {
          await this.moveToNextEntityForm({
            entityId: entityIdToUse,
            currentFormIndex: newFormIndex,
            init,
          });
        }
        const submitEntityFormCallback = async () => {
          entityCurrentForm?.submit();
          await moveToNextEntityFormCallback();
        }

        // if the form is fetched and empty/completed, we can submit it and move to the next one:
        // move to the next form if the Case Preservation is enabled if the preventAutoSubmit isn't set as true
        if (isSubmittedOnServer && this.isCasePreservationEnabled && !preventAutoSubmit) {
          await submitEntityFormCallback();
          // submit & store only forms of current entity that weren't stored to the server yet:
        } else if (!entityCurrentForm?.hasFields) {
          // Remove this check if we may need to submit forms of other entities:
          if (isCurrentEntity) {
            await entityFormAPI.submitEntityForm({
              case_id: this.tempCaseId,
              entity_id: entityIdToUse,
              tenant_check_id,
            });
          }
          await submitEntityFormCallback();
        }
      } catch (error) {
        console.error("Error moving to next entity form:", error);
        this.handleError(error);
      }
    },

    async onSelectEntity(id, params = {}) {
      const { init = true } = params; // re-init entity by default

      const canChangeTab = this.checkSwitchable(this.selectedEntity);
      if (!canChangeTab) return;
      await this.selectEntity(id, {
        hideEntityChangeNav: true,
        // submit: false,
        event: 'select-entity',
        init,
        ...params,
      });
      // await this.showProgressBar(id);
    },
    
    async initCurrentEntityFormList(params) {
      const {
        entityId,
        init = false, // true if called on page init
        preventAutoSubmit = false,
      } = params || {};

      const entityIdToUse = entityId || this.selectedEntityId;

      const formList = await this.fetchEntityFormList(entityIdToUse, params);
      await this.moveToNextEntityForm({
        entityId: entityIdToUse,
        init,
        preventAutoSubmit,
      });
      return formList;
    },

    async selectEntity(id, {
      init = false, // true if called on page init; set true to avoid auto redirections
      initCase = false, // true if called on the case creation init
      hideEntityChangeNav = true,
      // submit = false, // TODO check if we can remove this param
      event,
      // createEntity = false, // true if called right after entity creation
    } = {}) {
      // return callback to call it with the newly selected Entity:
      const initEntityMethods = {
        [CASE_CREATION_STEPS.ENTITY_DETAILS]: async () => {
           // show the 1st entity form:
          this.resetCurrentEntityFormIndex();

          // TODO store history BEFORE and AFTER forms separately to navigate through the whole flow
          // reset entity redirection configs history:
          this.setRedirectionScreenHistory();

          const entityFormList = await this.initCurrentEntityFormList({
            // submit,
            event,
            init,
            preventAutoSubmit: !init, // if executed by user interaction, we shouldn't auto submit forms
          });

          // Check redirection config on initialization (before form initialization) if:
          // - not all forms are stored (form list hasn't been submitted yet)
          // - Case Preservation is disabled (e.g., we are in revetting mode), because in this case we don't call the handleEntityChangeNav() method on initialization
          // - there is at least one form in the list. If there are no forms the entity workflow will be fetched immediately in onEntityFormListSubmitted()
          if ((!entityFormList?.isAllStored || !this.isCasePreservationEnabled) && entityFormList?.list?.length) {
            this.isRedirectionConfigBeforeForms = true;
            await this.fetchEntityWorkflow({
              init: true, // prevent history storage on initialization
            });
            // * if forms are already submitted the redirection config will be fetched after form initialization in onEntityFormListSubmitted()
            // * when in Revetting mode, all forms come as submitted so we PREVENT handleEntityChangeNav() AFTER entity initialization and EXECUTE BEFORE
          }

          // TODO check if we need to handle history in case of Revetting (Refreshing) mode
          // Fetch redirection history after entity form initialization.
          // * Note: if forms are submitted, fetchEntityWorkflow() will be called in onEntityFormListSubmitted() and if redirection config is received it will be added to the cached history. However, after the following API call, the relevant redirection config will be fetched from the server and handled on the FE if exists:
          await this.fetchCurrentEntityRedirectionScreenHistory();

          // if there is no forms for the selected entity, we can show Add Entity screen:
          if (!this.selectedEntityFormList?.length) {
            this.setAllEntityFormsStored(this.selectedEntityFormListInstance);
            this.forceShowEntityChangeNav = true;
          }

          const checkData = await this.fetchEntityChecks(id);
          const checkList = checkData?.data;
          this.$set(this.caseEntityChecks, id, checkList ?? []);
          await this.getProductMonitoring();
        },
      };
      // reset redirection config:
      await this.setRedirectionConfig();

      if (!id) this.selectedEntityId = null;
      else {
        this.selectedEntityId = id;

        // set selected entity globally:
        this.storeSelectedEntityGlobally();

        this.loaders.entity = true;

        this.forceShowEntityChangeNav = !hideEntityChangeNav;

        try {
          const entityCreationCallback = initEntityMethods[this.currentCaseCreationStep.id];

          if (this.selectedEntity) {
            await entityCreationCallback?.(this.selectedEntity);

            // reset errors list to show permission errors again for new entity:
            this.permissionErrors = [];
          }
        } catch (error) {
          this.handleError(error);
        }
        this.loaders.entity = false;
      }

      // TODO check if we need to handle it here:
      // Don't call handleEntityChangeNav if we initializing entity during the page (case) initialization
      const toHandleNavWhileInit = !init || (initCase && this.isCasePreservationEnabled);
      if (toHandleNavWhileInit && event !== 'select-entity' && event != 'add-entity') {
        await this.handleEntityChangeNav(this.selectedEntityId);
      }
    },

    setCaseEntitiesOrder(case_entities) {
      if (!this.caseEntitiesOrder) {
        const caseEntityIndex = case_entities.findIndex(
          (el) => el.id === this.caseTypeEntityId
        );
        const resultList = [...case_entities];
        const caseEntity = resultList.splice(caseEntityIndex, 1);
        resultList.unshift(...caseEntity);
        this.caseEntitiesOrder = resultList.map((i) => i.id);
      } else {
        case_entities.forEach((i) => {
          if (!this.caseEntitiesOrder.includes(i.id)) {
            this.caseEntitiesOrder.push(i.id);
          }
        });
      }
      return case_entities.reduce((acc, v) => {
        const index = this.caseEntitiesOrder.findIndex((x) => x == v.id);
        acc[index] = v;
        return acc;
      }, []);
    },

  /**
   * Hadle entities data
   *
   * @param {Object} payload entities data.
   * @param {Boolean} init is called while initial data creation
   */
    storeEntityData(payload, init = false) {
      const { case_entities } = payload;
      this.caseEntities = this.setCaseEntitiesOrder(case_entities);
      this.caseEntitiesData = payload;
      if (init) {
        // create empty forms to start monitoring their states:
        this.caseEntities.forEach((el) => {
          const entityFormList = new EntityFormList({
            entity: el,
          });
          // handle el.creation_form_submitted:
          entityFormList.setStored(!!el.creation_form_submitted); // TODO WLPRM-8655 check

          // TODO WLPRM-8655 init?
          this.$set(this.caseEntityForms, el.id, entityFormList);
        });
      }
    },

    async getCaseEntityData(init = false) {
      const { data } = await axios(`case/${this.tempCaseId}/entity`);
      const promises = data.case_entities
          .filter(entity=> getEntityTypeName(data, entity) != ENTITY_TYPE_NAMES.CASE)
          .map(entity => getEntityStatuses(entity.id));
  
      const entityStatuses = await Promise.all(promises);

      await this.fetchRiskLabelOptions({ case_id: this.tempCaseId });

      data.case_entities = data.case_entities.map(el => {
          el.statusList = entityStatuses.find(x => x.id == el.id)?.data || []; 
          return el;
      })          

      for (const key in data.case_entity_type_info) {
        if (data.case_entity_type_info[key].name === ENTITY_TYPE_NAMES.CASE) {
          const entity = data.case_entities.find((ent) => ent.entity_type_id === key)
          this.caseTypeEntityId = entity.id;
          break;
        }
      }
      await this.storeEntityData(data, init);
    },

    // refetch case package data if case already created (temCaseId exists)
    async refetchCasePackage() {
      // if no case id is set, we can't fetch data:
      if (!this.tempCaseId) return;
      
      // fetch package data for the current case:
      if (!this.getCaseData) {
        await this.fetchCaseData(this.tempCaseId);
      }

      // if no package data is stored:
      if (!this.computedSelectedPackageId) {
        this.computedSelectedPackageId = this.getPackageId;
        this.computedSelectedClientId = this.getClientId;
      }
    },

    // create case initial data
    async createTempCase() {
      this.tempCaseId = this.$route.query?.caseid;
      if (!this.tempCaseId) {
          const payload = {
            package_id: this.selectedPackageId,
            client_id: this.clientId,
          };
          const { data } = await axios.post(
            `case/create-multientity-case`,
            payload
          );

          // there are different ways to determine case id so we set it separately:
          this.tempCaseId = data?.id;

          if (this.tempCaseId)
            await this.fetchCaseData(this.tempCaseId), // TODO can be removed if getCaseData no longer used for the Check Output screens

          this.$router.push({
            query: { ...this.$route.query, caseid: this.tempCaseId },
          });
      }
      
      const promises = ([
        this.getCaseEntityData(true),
        this.fetchCaseStatus(),
        // this.fetchRevettingData(), // TODO check revetting data from server when BE is ready
      ]);

      await Promise.all(promises);
      
      // currently, isn't used
      // this.caseTypeEntityId = data.case_type_entity_id;
      // this.storeEntityData(data.entity_info, true);
    },

    async fetchStatus(id = this.tempCaseId) {
      // fetch entity's status list:
      const { data } = await getEntityStatuses(id);

      // update entity status:
      const relatedEntity = this.caseEntities.find((el) => el.id === id);
      if (relatedEntity)
        relatedEntity.statusList = data;

      return data;
    },

    getCurrentStatus(statusList) {
      return statusList.find((el) => el.current) ?? null;
    },

    async fetchCaseStatus() {
      try {
        this.caseStatus = await this.fetchStatus();
        // Dispatch to store to update case status list
        if (this.tempCaseId) {
          await this.$store.dispatch('fetchCaseStatusLists', { case_id: this.tempCaseId });
        }
      } catch (error) {
        this.handleError(error, false);
        this.caseStatus = null;
      }
    },

    async addEntityToCase() {
      this.tempCaseId = this.$route.query.caseid;
      await this.getCaseEntityData();
      this.forceShowEntityChangeNav = true;
    },

    async fetchEntityForm(case_entity_id, { tenant_check_id } = {}) {
      const formList = this.caseEntityForms[case_entity_id]?.originalFormList;
      let data = await entityFormAPI.fetchEntityForm({
        case_id: this.tempCaseId,
        entity_id: case_entity_id,
      }, { tenant_check_id }, formList);

      if (this.addEntityWorkflowOptions.silentEntityAddition && this.getNewEntityInfo?.entity_name) {
        data = prefillFormPreview(data, this.getNewEntityInfo.entity_name);
      } 

      // TODO WLPRM-8655 check:
      // add new form data to the corresponding entity form in the form list:
      this.caseEntityForms[case_entity_id]?.addFormData(tenant_check_id, data);
    },

    /**
     * Save FormBuilder section method
     *
     * @param {String} section_id
     * @param {Object} data // saved section data
     * @param {Boolean} finalSubmit
     */
    async saveEntitySection(payload) {
      this.formStates.loading = true;
      let sectionSaved = false;

      try {
        const existingIndex = this.duplicateEntityPayload.findIndex(item => 
            item.section_id === payload.section_id
          );
    
        if (this.selectedEntity.type.toLowerCase() !== 'case') {
          if (existingIndex !== -1) {
            // Replace the existing section data
            this.$set(this.duplicateEntityPayload, existingIndex, {
              section_id: payload.section_id,
              data: payload.data,
              finalSubmit: payload.finalSubmit,
            });
          } else {
            // Add the section if it doesn't exist
            this.$set(this.duplicateEntityPayload, this.duplicateEntityPayload.length, {
              section_id: payload.section_id,
              data: payload.data,
              finalSubmit: payload.finalSubmit,
            });
          }
        }
        const tenant_check_id = this.selectedEntityCurrentFormListItem?.payload?.tenant_check_id;
        sectionSaved = await entityFormAPI.saveEntityFormSection({
          case_id: this.tempCaseId,
          entity_id: this.selectedEntity.id,
          data: payload,
          tenant_check_id,
        });
        
        // Update entity's form with new values from FormBuilder:
        this.selectedEntityCurrentForm?.updateSectionData(payload.data);
      } catch (error) {
        this.handleError(error);
        sectionSaved = false;
      }
      this.formStates.loading = false;
      return sectionSaved;
    },

    async getDuplicateEntities(payload) {
      this.formStates.loading = true;
      let sectionSaved = false;
      try {
        sectionSaved = await entityFormAPI.getDuplicateEntities({
          case_id: this.tempCaseId,
          entity_id: this.selectedEntity.id,
          data: payload,
        });

      } catch (error) {
        this.handleError(error);
        sectionSaved = false;
      }
      this.formStates.loading = false;
      return sectionSaved;
    },

    changeToaddEntityFlow() {
      this.forceShowEntityChangeNav = true;
    },
    
    isCaseEntity(id) {
      return id === this.caseEntity?.id;
    },

    // currently, is used to recheck entity state (including workflow)
    // call when ALL entity forms are submitted
    async onEntityFormListSubmitted({
      init = false,
      refetchForms = true,
    } = {}) {
      // errors are handled on a higher level
      // skip the "Case" entity
      const toFetchWorkflow = !this.isCaseEntitySelected;
      if (toFetchWorkflow) {
        this.loaders.redirection = true;
      }

      // reset redirection config:
      await this.setRedirectionConfig();
      this.isRedirectionConfigBeforeForms = false;

      await this.getCaseEntityData(); 

      // reset globally stored entity:
      this.storeSelectedEntityGlobally();

      if (!refetchForms) {
        // refetch entity form list to actualize forms' data:
        await this.selectedEntityFormListInstance?.refetchForms({
          case_id: this.tempCaseId,
          entity_id: this.selectedEntityId,
        });
        // * the last form may be excluded from the list to refetch since it is already actualized
      }

      // fetch redirection config
      if (toFetchWorkflow) {
        await this.fetchEntityWorkflow({ init });
      }
      
      if (!init) {
        // prevent nav change on init as it has already been set:
        await this.handleEntityChangeNav(this.selectedEntityId);
      }

      // In case we need updated entity form data we should store updated data from the FormBuilder. Currently, all updated data is available until FormBuilder is rerendered
    },

    async submitEntityForm(preventAutoSubmitNext = false) {
      try {
        const tenant_check_id = this.selectedEntityCurrentFormListItem?.payload?.tenant_check_id;

        await entityFormAPI.submitEntityForm({
          case_id: this.tempCaseId,
          entity_id: this.selectedEntity.id,
          tenant_check_id,
        })
        this.selectedEntityCurrentForm?.submit();
        const moveNextPromise = this.moveToNextEntityForm({
          preventAutoSubmit: preventAutoSubmitNext,
        });
        const refreshFormPromise = this.fetchEntityForm(
          this.selectedEntity.id, 
          { tenant_check_id }
        );
        await Promise.all([
          moveNextPromise,
          refreshFormPromise,
        ]);
      } catch(error) {
        this.handleError(error);
      }
    },

    getDuplicateEntitiesPayload() {
      const list = this.selectedEntityFormList.map(i => i.form.formData.sections).flat();
      const sectionsToAdd = list
        .filter(sec => !this.duplicateEntityPayload.find(dupSec => sec.section_id === dupSec.section_id))
        .map((i) =>({ data: i }))

      const fullSectionList = [
        ...sectionsToAdd,
        ...this.duplicateEntityPayload
      ]

      const order = list.map(i => i.section_id);

      const orderedSections = entityFormAPI.sortByOrderArr({ 
        orderArray: order, 
        arr: fullSectionList, 
        keyName: 'section_id' 
      });      

      return orderedSections;
    },

    async submitEntityFormList() {
      // The form submission is managed by the moveToNextEntityForm() method

      // Use if we need to prevent some API calls:
      // const isAllFormsAlreadyStored = this.selectedEntityFormListInstance?.isAllStored;

      this.setAllEntityFormsStored(this.selectedEntityFormListInstance);

      // if there is no next form, we can submit the entity form list
      if (this.isDuplicateCheckInProgress) {
        return;
      }
      this.isDuplicateCheckInProgress = true;
      this.formStates.loading = true;
      try {
        if (this.selectedEntity?.type.toLowerCase() === 'person' || this.selectedEntity?.type.toLowerCase() === 'company') {
          this.loadingState.pendingApproval = true;

          let duplicateDetails;
          if (checkPermission('case.create_duplicate_entities') && !this.isRevettingMode) {
            const duplicatedEntities = this.getDuplicateEntitiesPayload();
            duplicateDetails = await this.getDuplicateEntities(duplicatedEntities);
            this.entityName = duplicateDetails.entity_name;
            this.rows = duplicateDetails.data;
          }
          if (this.rows?.length > 0 && checkPermission('case.create_duplicate_entities')) {
            this.loadingState.pendingApproval = true;
            this.$nextTick(() => {
            this.showDupicateEntity = true;
          });
            this.loadingState.pendingApproval = false;

          } else {
            this.isDuplicate = false;
            await entityFormAPI.submitEntityFormList({
              case_id: this.tempCaseId,
              entity_id: this.selectedEntity.id,
              is_duplicate: this.isDuplicate,
            });
            this.$emit('submitAddEntity');
            await this.onEntityFormListSubmitted();
            this.duplicateEntityPayload = [];
          }
        }
        else {
          this.isDuplicate = false;
          await entityFormAPI.submitEntityFormList({
            case_id: this.tempCaseId,
            entity_id: this.selectedEntity.id,
            is_duplicate: this.isDuplicate,
          });
          this.$emit('submitAddEntity');
          await this.onEntityFormListSubmitted();
        }

      } catch (error) {
        this.handleError(error);
      }
      this.formStates.loading = false;
      this.isDuplicateCheckInProgress = false; // Reset the flag
    },

    async saveEntity() {
      this.formStates.loading = true;
      this.isCreatingEntity = true;
      this.isDuplicate = true;
      try {
          await entityFormAPI.submitEntityFormList({
            case_id: this.tempCaseId,
            entity_id: this.selectedEntity.id,
            is_duplicate: this.isDuplicate,
          });
          this.$emit('submitAddEntity');
          this.isCreatingEntity = false;
          this.showDupicateEntity = false;
          await this.onEntityFormListSubmitted();
          this.rows = [];
      } catch (error) {
        this.handleError(error);
      }
      this.formStates.loading = false;
      this.duplicateEntityPayload = [];
    },

    getEntityCtx(id) {
      if (!id || !this.caseEntitiesData) return null;

      const { case_entities } = this.caseEntitiesData;
      const entity = case_entities.find(x => x.id == id);

      return  {
        isCaseEntity: this.isCaseEntity(id),
        isSubmitted: entity?.creation_form_submitted || false, // TODO WLPRM-8655 check
        hasForm:  this.caseEntityForms?.[id].hasFields
      }
    },

    async handleEntityChangeNav(id) {
      if (!id) return;

      if (this.isNextEntity) {
        const payload = {};
        if (this.isRevettingMode) {
          // Prevent auto-redirection to the next entities and behave as if the entity was selected manually when in Revetting mode:
          payload.event = "select-entity";
        }
        await this.selectNextEntity(payload);
        return;
      }

      const { 
        isCaseEntity,
        isSubmitted, 
        hasForm
      } = this.getEntityCtx(id);

      const processEntityNav = async () => {
        if (this.toAddSingleTypeEntity) {
            await this.addEntity({ id: this.singleTypeCaseEntity.id })
        } else {
          this.forceShowEntityChangeNav = true;
        }
      }

      if (isCaseEntity) {
        if (!hasForm) { 
          if (this.entitiesCounter.total.count == 1) {// prevents create entity on page referesh
            await processEntityNav();
          }
        } else if (hasForm  && isSubmitted) {
          await processEntityNav();
        }
      } else if (!isCaseEntity && isSubmitted) {
        const isMaxEntitiesReached = !this.entitiesToAdd.length;
        const isLastRevettingEntity = this.isRevettingMode && this.isLastEntity;
        const autoNextOptional = [
          isMaxEntitiesReached,
          isLastRevettingEntity,
        ];
        const autoNextRequired = [
          !this.isRedirection,
          !this.isRedirectionConfigBeforeForms, // prevent processing if forms still haven't been reached
        ];
        const isAutoNextRequiredMet = autoNextRequired.every(Boolean);
        const isAutoNextAllowed = autoNextOptional.some(Boolean) && isAutoNextRequiredMet;

        // Exit case creation & redirect to the "Input" tab if addEntityWorkflow and no more redirection configs:
        if (isAutoNextRequiredMet && this.checkCaseCreationExit()) return;

        else if (isAutoNextAllowed) { // case creation exit
          this.nextStep();
        } else if (this.entitiesToAdd.length && !this.redirectionConfig) { // default non case entity type
          await processEntityNav();
        }
      }
    },

    async addEntity(payload) {
      this.duplicateEntityPayload = [];
      const { id, entity_name } = payload; // entity_type_id

      try {
        let currentEntityIndex = this.caseEntities.length - 1;
        
        if (this.selectedEntityId) {
          const selectedEntityIndex = this.caseEntities.findIndex((el) => el.id === this.selectedEntityId);
          if (selectedEntityIndex != -1) currentEntityIndex = selectedEntityIndex;
        }

        this.$set(this.addEntityLoaders, id, true);
        const { data } = await axios.post(`case/entity`, {
          case_id: this.tempCaseId,
          entity_type_id: id,
        });

        const newEntity = {
          ...this.selectedEntity?.data,
          id: data.case_entity_id,
          form_id: data.form_id,
          active: false,
          created_at: null,
          entity_name: entity_name ?? null,
          entity_type_id: id,
          form_status_id: null,
          form_status_updated_at: null,
        };
        // await this.showProgressBar(newEntity?.id)
        delete newEntity.statusList;
        delete newEntity.is_main_entity;

        this.caseEntities.splice(currentEntityIndex + 1, 1, newEntity);
        this.$emit("addEntity", {
            id: newEntity.id,
            name: newEntity.entity_name || "",
            entity_type:  this.caseEntityTypesInfo[newEntity.entity_type_id]?.name,
        });

        // create empty forms to start monitoring their states:
        const entityFormList = new EntityFormList();
        this.$set(this.caseEntityForms, newEntity.id, entityFormList);

        // store entity check list:
        this.$set(this.caseEntityChecks, newEntity.id, []);

        await this.selectEntity(newEntity.id, {
          hideEntityChangeNav: true, 
          submit: false,
          event: this.addEntityWorkflowOptions.silentEntityAddition ? 'silentEntityAddition' : 'add-entity',
          createEntity: true,
        });
        await this.fetchRiskScore({ case_id: this.tempCaseId });

        if (this.addEntityWorkflowOptions.silentEntityAddition) {
          this.resetNewEntityInfo();
        }
      } catch (error) {
        this.handleError(error);
      }
      this.$set(this.addEntityLoaders, id, false);
    },

    async removeEntity(payload) {
      const { id } = payload; // entity_type_id
      const isCurrent = this.selectedEntityId === id;
      const currentEntityIndex = this.caseEntities.findIndex(
        (el) => el.id === id
      );
      const currentComputedEntityIndex = this.computedEntitiesList.findIndex(
        (el) => el.id === id
      );
      try {
        const removeConfirmed = await this.$refs["confirm-popup"].show({
          title: "Are you sure?",
          message:
            "This entity will be deleted permanently. Please confirm to continue deleting this entity.",
        });
        if (removeConfirmed) {
          await axios.delete(`case/${this.tempCaseId}/entity/${id}`);
          // select previous entity if removed was selected
          if (isCurrent)
            await this.selectEntity(this.caseEntities[currentComputedEntityIndex - 1].id, { hideEntityChangeNav: false, submit: true });
          // remove entity from list:
          this.caseEntities.splice(currentEntityIndex, 1);
          this.caseEntitiesOrder = this.caseEntitiesOrder.filter(x => x!=id);
          // remove related form:
          delete this.caseEntityForms[id];
        }
      } catch (error) {
        this.handleError(error);
      }
    },

    getNextEntityIndex(action) {
      let nextIndex = 0;
      const currentEntityIndex = this.computedEntitiesList.findIndex(
        (el) => el.id === this.selectedEntityId
      );
      if (action != "stepInited") {
        nextIndex = currentEntityIndex + 1;
      }

      let enabledIndex;
      for (let i = nextIndex; i < this.computedEntitiesList.length; i++) {
        const isSameIndex = i === currentEntityIndex;
        if (!this.computedEntitiesList[i].disabled && !isSameIndex) {
          enabledIndex = i;
          break;
        }
      }
      return enabledIndex;
    },

    async selectNextEntity(payload) {
      const { event = "next-entity" } = payload || {};

      const entityIndex = this.getNextEntityIndex();
      if (entityIndex) {
        await this.selectEntity(this.computedEntitiesList[entityIndex].id, {
          event,
        });
        return;
      }

      const nextStep =
        this.caseCreationSteps.findIndex(
          (x) => x.id == this.currentCaseCreationStep.id
        ) + 1;
      if (nextStep) {
        this.selectStep(this.caseCreationSteps[nextStep]);
      }
    },

    submitCaseCallback() {
      this.$router.push({ name: "dashboard.view" });
    },

    async submitCase() {
      if (this.isCaseSubmitted) return;

      // use to provide steps callbacks:
      const stepCallbacks = {};
      const { id: stepID } = this.currentCaseCreationStep;

      try {
        await stepCallbacks[stepID]?.();
        await axios.post(`case/${this.tempCaseId}/submit-multientity-case`);

        this.isCaseSubmitted = true;
        // check if there is redirection after submit
        await this.fetchEntityWorkflow();
        await this.handleEntityChangeNav(this.selectedEntityId);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loaders.caseSubmission = false;
      }
    },

    // Call if we need to force exit the CaseCreationWorkflow
    // e.g., on the Case Screen page:
    checkCaseCreationExit() {
      // exit the CaseCreationWorkflow if there is no more redirections in the current workflow (e.g., on the Case Screen page):
      if (this.addEntityWorkflow) {
        this.$emit("redirectToInput", false);
        return true;
      }
    },

    startCaseCreation() {
      this.currentCaseCreationStep = this.caseCreationSteps[0];
    },

    async fetchEntityWorkflow(payload = {}) {
      // use the case id if work with particular entities has been finished:
      const object_id = payload.object_id ?? this.currentWorkflowObjectId;

      const {
        init,
        isNext = false, // true if called after previous redirection screen submission
      } = payload;

      try {
        this.loaders.redirection = true;

        const { data } = await axios.get(
          `workflow/next-ui`,
          { params: { object_id } }
        );

        const redirectionData = data?.data;
        const redirectionConfig = redirectionData.redirection_config;

        const currentScreenSettings = getCurrentScreenSettings(redirectionConfig);
        const isScreenConfigured = !!currentScreenSettings;
        const checkId = redirectionData?.redirection_config?.sub_configuration?.integration_source_id;

        if (!this.caseEntityChecks?.[this.selectedEntityId]) {
          const id = this.selectedEntityId;
          const checkData = await this.fetchEntityChecks(id);
          const checkList = checkData?.data;
          this.$set(this.caseEntityChecks, id, checkList ?? []);
        }

        if (checkId) {
          const check = this.caseEntityChecks?.[this.selectedEntityId]?.find((chk) => chk.integration_source_id === checkId);
          this.$router.push({
            query: { 
              ...this.$route.query, 
              check_id: check?.id,
              check_name: getIntegratedToolName(check.check_name),
            },
          })
        }

        if (isScreenConfigured) {
          const displayType = getRedirectionDisplay(redirectionData);
          await this.setRedirectionConfig({
            type: displayType,
            payload: redirectionData,
            init,
            isNext,
          });
        } else {
          // reset redirection screens if there is no more redirections:
          await this.setRedirectionConfig({ init, isNext });

          await this.ackRedirectionScreen(redirectionData.ack_id); // fallback
        }
      } catch (error) {
        await this.setRedirectionConfig();
        if (error.response?.data?.status !== false) throw error;
        // else there is no redirection/workflow configured for this entity
      } finally {
        this.loaders.redirection = false;

        if (!this.redirectionConfig && this.isCaseSubmitted) {
          // Cancel revetting on the server:
          if (this.isRevettingMode) {
            await this.switchRevetting(false);
          }

          this.submitCaseCallback();
        }
      }
    },

    // acknowledge redirection screen (according to configured workflow to te entity)
    async ackRedirectionScreen(ack_id) {
      if (!ack_id) return;
      await axios.post(
          `workflow/next-ui-ack?ack_id=${ack_id}`,
          // { ack_id }
        );
    },
    setRedirectionScreenHistory(reset = false) {
      const object_id = this.currentWorkflowObjectId;
      if (reset || !object_id) {
        this.currentEntityRedirectionScreenHistory = null;
        return;
      }
      this.currentEntityRedirectionScreenHistory = new RedirectionConfig(this.tempCaseId, object_id);
      return this.currentEntityRedirectionScreenHistory;
    },
    async navigateRedirectionScreenBack() {
      if (!this.currentEntityRedirectionScreenHistory?.history.length || !this.redirectionScreen) {
        await this.resetRedirectionScreenHistory();
        return;
      }

      // currentConfig must already be stored to the this.redirectionScreen // TODO check
      this.currentEntityRedirectionScreenHistory.popHistory();
      const newConfig = this.currentEntityRedirectionScreenHistory?.history?.at(-1);

      await this.ackRedirectionScreen(this.redirectionScreen.ack_id);

      if (!newConfig) {
        // reset redirection config if no previous config is found:
        await this.resetRedirectionScreenHistory();
      } else {
        const displayType = getRedirectionDisplay(newConfig);
        await this.setRedirectionConfig({
          type: displayType,
          payload: newConfig,
          history: true,
        });
      }
    },

    async resetRedirectionScreenHistory() {
      this.setRedirectionScreenHistory();
      await this.setRedirectionConfig();
    },
    async resetEntityRedirectionHistory(storeToServer = false) {
      this.currentEntityRedirectionScreenHistory?.setHistory();
      if (storeToServer) {
        await this.currentEntityRedirectionScreenHistory?.storeHistory();
      }
    },

    async setRedirectionConfig(redirectionConfigPayload) {
      const {
        type,
        payload = null,
        history = false, // indicate if the config is selected from the history
        // init = false, // indicate if the config is set on entity init
        isNext = false, // true if called after previous redirection screen submission
      } = redirectionConfigPayload || {};

      if (!payload && !type) {
        this.redirectionConfig = null;
        this.currentEntityRedirectionScreenHistory?.setHistory();

        if (isNext) {
          // If called while moving to the next redirection screen, store a cleared history on the server.
          await this.currentEntityRedirectionScreenHistory?.storeHistory();
        }
        
        return;
      } else if (type === redirectionDisplayVariants.REDIRECT) {
        const redirectionId = payload?.ack_id;
        const redirectionConfig = payload?.redirection_config?.configuration;
        const redirectionSubConfig = payload?.redirection_config?.sub_configuration;
        const routeParams = subScreenList[redirectionConfig]?.(redirectionSubConfig, this.app);

        if (redirectionId && routeParams) {
          const promises = [
            this.ackRedirectionScreen(redirectionId), // acknowledge redirection config if required action will be performed immediately
            this.resetEntityRedirectionHistory(true), // reset redirection history as it is no longer needed
          ];
          await Promise.all(promises);
          // redirect to the appropriate screen:
          this.$router.push(routeParams);
        }
        return;
      }

      else if (!this.redirectionConfig) {
        // init redirection config if it was empty:
        this.redirectionConfig = {};
      }

      const redirectionConfigData = payload ? {
        ...payload,
        type,
      } : null;

      if (redirectionConfigData) {
        this.$set(this.redirectionConfig, type, {
          ...redirectionConfigData,
          loaders: {
            submission: false,
          },
        });

        // if selected from history, we should not add it to the history:
        if (!history)
          this.currentEntityRedirectionScreenHistory.addHistory([payload]);

      } else {
        // reset redirection config if no payload is provided:
        this.$set(this.redirectionConfig, type, null);
      }
      // store updated history if next redirection screen has been fetched
      // or if we want to store updated history while moving back.
      // if !isNext there is no need to store it to the history as it is already stored (just fetched from server)
      if (isNext || history) {
        await this.currentEntityRedirectionScreenHistory.storeHistory();
      }
    },

    async cancelRedirectionScreen(redirectionConfig = {}) {
      const displayType = getRedirectionDisplay(redirectionConfig);
      await this.setRedirectionConfig({
        type: displayType,
      }); // reset appropriate redirection config
    },

    async submitRedirectionScreen(redirectionConfig = {}) {
      const { ack_id } = redirectionConfig;
      if (!ack_id) return;

      try {
        redirectionConfig.loaders.submission = true;
        await this.ackRedirectionScreen(ack_id);
        // refetch current entity status:
        await this.fetchStatus(this.selectedEntityId);

        const promises = [
          this.fetchEntityWorkflow({
            isNext: true, // fetch next redirection config after the previous one is submitted
          }), // check if there is another redirection config
          this.fetchCaseStatus(), // refetch case status
          // this.showProgressBar(this.selectedEntity.id)
        ];
        await Promise.all(promises);
        await this.handleEntityChangeNav(this.selectedEntityId);
      } catch (error) {
        this.handleError(error);
      } finally {
        redirectionConfig.loaders.submission = false;
      }
    },

    async onRedirectionScreenError(error, redirectionConfig = {}) {
      // hide current redirection screen while refetching entity workflow
      this.loaders.redirection = true;

      // store received permission error to not show it again for the same entity:
      if (error instanceof PermissionError && !this.permissionErrors.some((err) => err.permission === error.permission)) {
        this.handleError(error);
        this.permissionErrors.push(error);
      }

      // check if there is another redirection config:
      await this.submitRedirectionScreen(redirectionConfig);
      // * acknowledge UI Redirection separately instead of submission method calling if another logic should be implemented

      this.loaders.redirection = false;
    },

    storeSelectedEntityGlobally() {
      this.setSelectedEntity({
        ...this.selectedEntity?.monitoringData,
        ...this.selectedEntity?.data,
        checks: this.selectedEntity?.checks,
      });
    },

    async fetchRiskScore(...args) {
      if (!this.isRiskManagementEnabled) return;
      await this.fetchRiskScoreMethod(...args);
    },

    async fetchEntityChecks(entity_id) {
      if(entity_id) {
      let url = `case/${this.tempCaseId}/case_entity/${entity_id}/check`;
      if (this.app === appList.CLIENT) {
        url = `case/${this.tempCaseId}/case_entity/${entity_id}/check/client`;
      }
      const { data } = await axios(url);
      return data;}
    },

    // Is called both when navigate back from the entity form and from the redirection screen with "form" type
    async selectPreviousEntityFormSection(prevSectionData = {}) {
      const { toIndex, fromIndex, from: fromSection } = prevSectionData;
      const isEntityFormsChanging = !fromIndex &&  fromIndex < 1;

      this.preserveCurrentFormSection(fromSection);

      if (toIndex < 0 && this.currentEntityFormIndex > 0) {
        this.setCurrentEntityFormIndex(this.currentEntityFormIndex - 1);
      }

      try {
        if (isEntityFormsChanging) {
          const currentComputedEntity = this.getEntityById(this.selectedEntityId);
          const prevFormPayload = currentComputedEntity?.formList?.list?.[this.currentEntityFormIndex]?.payload
            || null;
          const requestParams = {};
          if (prevFormPayload?.tenant_check_id) {
            requestParams.tenant_check_id = prevFormPayload.tenant_check_id;
          }

          await this.fetchEntityForm(this.selectedEntityId, requestParams);
        }
      } catch (error) {
        this.handleError(error);
      }
    },

    preserveCurrentFormSection(section) {
      if (!this.selectedEntityCurrentForm) return;
      this.selectedEntityCurrentForm.updateSectionData(section);
    },

    setCurrentEntityFormIndex(index) {
      // * If the form data isn't fetched yet, fetch it here.
      // Currently, all previous forms are prefetched.
      this.currentEntityFormIndex = index;
    },

    async switchRevetting(value) {
      const switchRevettingMethod = value
        ? this.startRevetting
        : this.stopRevetting;
      try {
        await switchRevettingMethod(this.tempCaseId);

        if (!value) {
          await this.updateRefreshConfig();
        }
      } catch (error) {
        console.error("Error switching revetting:", error);
      }
    },

    async updateRefreshConfig() {
      if (!this.isOneOfRefreshMode) return;

      const { mode, one_off_date } = this.refreshConfig ?? {};

      await this.updateRefreshConfiguration({
        case_id: this.tempCaseId,
        active: false,
        mode,
        one_off_date,
      });
    },

    async fetchCurrentEntityRedirectionScreenHistory() {
      if (!this.currentEntityRedirectionScreenHistory) {
        console.error("Redirection screen history instance is not set.");
      }

      await this.currentEntityRedirectionScreenHistory.fetchHistory();

      const currentConfig = this.currentEntityRedirectionScreenHistory.currentRedirectionConfig;
      if (currentConfig) {
        const displayType = getRedirectionDisplay(currentConfig);

        // If there is no redirection config for the current display type, set it from history:
        if (!this.redirectionConfig?.[displayType]) {
          await this.setRedirectionConfig({
            type: displayType,
            payload: currentConfig,
            history: true, // prevent double adding to the history
          });
        }
      }
    }
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/functions.scss";

.submit-confirmation-text {
  list-style: disc;
  padding-left: toRem(20px);
}

.case-creation__tabs {
    padding-bottom: 15px
}

.case-creation__tabs--wrapper > .neo-layout {
  display: flex;
  flex: 0 1;
  flex-direction: column;
}

.case-creation__tabs--tab-content  {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow:hidden;
}

.neo-form-builder {
  max-height:100%!important;
}

.custom-table-container {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
}

.dob-header {
  position: relative;
  padding-bottom: 30px !important;
  text-align: center !important;
}

.dob-subheaders {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  border-top: 1px solid #ddd;
  padding-top: 5px;
  font-weight: normal;
  font-size: 0.9em;
  text-align: center !important;
}

.dob-cell {
  text-align: center !important;
}

.custom-table th, .custom-table td {
  padding: 8px;
  border: 1px solid #ddd;
  text-align: left;
}

/* Override for DOB cells */
.custom-table th.dob-header, 
.custom-table td.dob-cell {
  text-align: center !important;
}

</style>
